'use client';

import { useRouter } from 'next/navigation';

interface LocationCardShellProps {
  /** Where the card goes. Omit to render an inert card. */
  href?: string | null;
  /** Open in a new tab (external destinations, e.g. Google Maps). */
  external?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/**
 * Did this event start on something that handles its own activation? Anchors and
 * buttons do; the photo, the card padding and the plain text do not.
 *
 * The match must be INSIDE this card: closest() keeps walking past the shell, so
 * an unscoped check would make a shell nested in any link or button swallow
 * every click on itself — silently, with nothing to trace.
 */
function fromNestedControl(e: React.MouseEvent) {
  // Element, not HTMLElement: a click on an arrow lands on the lucide <svg>.
  const control = (e.target as Element | null)?.closest(
    'a[href], button, input, select, textarea, [role="button"], [role="link"]',
  );

  return control != null && e.currentTarget.contains(control);
}

/**
 * The clickable wrapper every location card uses.
 *
 * A location card can't simply be one big <a>: it contains an ImageCarousel
 * whose arrows and dots are real <button>s, and interactive content inside an
 * anchor is invalid HTML. Nor can the anchor be stretched over the photo with
 * `::after` — the overlay would swallow the pointer events the gallery needs
 * for hover-pause and thumb swipe. So the photo belongs to the gallery, and
 * this shell restores the "tap anywhere on the card" affordance around it.
 *
 * ACCESSIBILITY: this deliberately has no role/tabIndex. It is a pointer
 * convenience layered on top of a card that MUST already contain a real anchor
 * on its text — that anchor is the keyboard and screen-reader affordance, and
 * giving the shell a second one would just add a duplicate tab stop to the same
 * destination. Callers are responsible for that inner link.
 *
 * Modifier and middle clicks are honoured so the card behaves like the anchor
 * it replaces: cmd/ctrl/shift-click and middle-click open a new tab instead of
 * navigating away — which matters in a directory of ~100 venues.
 *
 * Nested controls win. A click that started on a descendant link or button is
 * that control's business, so the shell stays out of it: without this the card's
 * own title link would navigate AND the shell would window.open the same URL,
 * opening two tabs, and middle-clicking a carousel arrow would open the venue.
 * Guarding here rather than at each call site keeps new callers correct by
 * default — and auxclick in particular cannot be stopped by a caller's onClick.
 *
 * A client component on purpose: the website-builder blocks that use it are
 * universal (server-rendered), and a handler cannot be passed from there.
 */
export function LocationCardShell({
  href,
  external = false,
  className,
  style,
  children,
}: LocationCardShellProps) {
  const router = useRouter();

  if (!href) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  const openInNewTab = () => window.open(href, '_blank', 'noopener,noreferrer');

  return (
    <div
      className={className}
      style={style}
      onClick={(e) => {
        if (e.button !== 0 || e.defaultPrevented || fromNestedControl(e)) {
          return;
        }

        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          openInNewTab();
          return;
        }

        if (external) {
          openInNewTab();
          return;
        }

        router.push(href);
      }}
      // Middle-click fires auxclick, never click, so it needs its own handler —
      // and its own nested-control guard, since a caller's onClick can't stop it.
      onAuxClick={(e) => {
        if (e.button !== 1 || e.defaultPrevented || fromNestedControl(e)) {
          return;
        }

        // Only generic content reaches here (controls bailed above), so this
        // cancels nothing in most browsers — it is for the ones that do act on
        // a middle click over plain content, e.g. Firefox's middlemouse.* prefs.
        e.preventDefault();
        openInNewTab();
      }}
    >
      {children}
    </div>
  );
}
